import { createContext, useContext, useMemo, useState } from 'react';
import { LocationOption } from 'server/internal-services/public-bff.interface';
import { LocationMessageType } from 'types';
import { useFeatureFlags } from './FeatureFlagProvider';
import { Body, useLoading, Modal, ModalBody } from '@teko/ds-desktop';
import { isServerless, reloadPage, setUserSelectedInfo } from 'helpers';
import {
  SESSION_STORAGE_LOCATION_HINT_SHOWED,
  SESSION_STORAGE_LOCATION_MESSAGE,
  userSelectedConstants,
} from '../constants';
import { t } from 'i18n';
import dynamic from 'next/dynamic';
import { useGlobalData } from './GlobalDataContext';
export interface LocationContextModel {
  locations: LocationOption[];
  isShowed?: boolean;
  setShowed: (isShowed: boolean) => void;
  onSelectLocation?: (location: LocationOption) => void;
  showRegionPickerSuggestionPopover: boolean;
  onCloseRegionPickerSuggestionPopover: () => void;
  selectedLocation?: LocationOption;
  enabled?: boolean;
  loading?: boolean;
}

const LocationPicker = dynamic(() =>
  import('@teko/teko-ecommerce').then((mod) => mod.LocationPicker),
);

const LocationContext = createContext<LocationContextModel>({
  locations: [],
  onCloseRegionPickerSuggestionPopover: () => undefined,
  setShowed: () => {},
  showRegionPickerSuggestionPopover: false,
  loading: true,
});

export interface LocationProviderProps {
  children: React.ReactNode;
}
export const useLocation = () => {
  return useContext(LocationContext);
};

export const LocationProvider: React.FC<LocationProviderProps> = ({
  children,
}) => {
  const {
    isMobile,
    location: selectedLocationCode,
    locations = [],
  } = useGlobalData();
  const [, setLoading] = useLoading('location');
  const [show, setShow] = useState<boolean>(false);
  const { flags } = useFeatureFlags();

  const regionPickerSuggestionPopoverClosed =
    isServerless() &&
    sessionStorage?.getItem(SESSION_STORAGE_LOCATION_HINT_SHOWED);
  const showRegionPickerSuggestionPopover =
    !regionPickerSuggestionPopoverClosed &&
    isServerless() &&
    sessionStorage.getItem(SESSION_STORAGE_LOCATION_MESSAGE) ===
      LocationMessageType.Hint &&
    flags?.epic_ecom_284;
  const hideSearchBox = locations.length <= 10;

  const onSelectLocation = (location: LocationOption) => {
    if (isServerless()) {
      setUserSelectedInfo(
        userSelectedConstants.LOCATION,
        location.locationCode,
      );
      setLoading(true);
      setShow(false);
      reloadPage();
    }
  };

  const enabled = locations.length > 1 && flags?.epic_ecom_284;
  const loading = !isServerless();

  const onCloseRegionPickerSuggestionPopover = () => {
    sessionStorage.setItem(SESSION_STORAGE_LOCATION_HINT_SHOWED, 'true');
  };

  const selectedLocation = useMemo(
    () =>
      locations.find(
        ({ locationCode }) => locationCode === selectedLocationCode,
      ),
    [locations, selectedLocationCode],
  );

  return (
    <LocationContext.Provider
      value={{
        locations,
        isShowed: show,
        setShowed: setShow,
        onSelectLocation,
        showRegionPickerSuggestionPopover,
        onCloseRegionPickerSuggestionPopover,
        selectedLocation,
        enabled,
        loading,
      }}
    >
      <Modal
        width={isMobile ? 300 : 400}
        visible={
          isServerless() &&
          sessionStorage.getItem(SESSION_STORAGE_LOCATION_MESSAGE) ===
            LocationMessageType.Error
        }
        onOk={() => {
          reloadPage();
        }}
        okText={t('Thử lại')}
        cancelText=""
        showCloseIcon={false}
        title={t('Thông báo')}
        centeredTitle
        alignFooter="center"
      >
        <ModalBody padding={['s0', 's0']}>
          {
            <Body level={1} style={{ textAlign: 'center' }}>
              {t(
                'Hiện tại không lấy được thông tin địa điểm để hiển thị sản phẩm phù hợp với bạn. Vui lòng thử lại hoặc quay lại sau giây lát!',
              )}
            </Body>
          }
        </ModalBody>
      </Modal>

      {children}

      {show && (
        <LocationPicker
          isShowed={show}
          isMobile={isMobile}
          setShowed={setShow}
          locations={locations}
          onSelectLocation={onSelectLocation as never}
          selectedLocation={selectedLocation}
          hideSearchBox={hideSearchBox}
        />
      )}
    </LocationContext.Provider>
  );
};
